import { useMemo, useState } from 'react';
import { UseFormReturn, useController } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select
} from '@mui/material';

import FacilitySearch from '@/components/FacilitySelector/FacilitySearch';

import { DefaultScreenerViewOption } from './defaultScreenerViewOptions';

const isKeyOwnedBySearchBox = (key: string) =>
  key.length === 1 || key === 'Home' || key === 'End';

interface DefaultScreenerViewSelectProps {
  control: UseFormReturn<any>['control'];
  name: string;
  options: DefaultScreenerViewOption[];
  isLoading?: boolean;
}

export function DefaultScreenerViewSelect({
  control,
  name,
  options,
  isLoading
}: DefaultScreenerViewSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const {
    field: { value, onChange, ...field }
  } = useController({ name, control });

  const matchingOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query
      ? options.filter((option) => option.label.toLowerCase().includes(query))
      : options;
  }, [options, searchQuery]);

  const selectedOption = options.find((option) => option.id === value);
  const isSelectedOptionHidden =
    !!selectedOption &&
    !matchingOptions.some((option) => option.id === selectedOption.id);

  return (
    <FormControl fullWidth disabled={isLoading}>
      <InputLabel id={`${name}-label`}>Default Screener View</InputLabel>
      <Select
        {...field}
        labelId={`${name}-label`}
        label="Default Screener View"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        renderValue={() => selectedOption?.label ?? ''}
        onClose={() => setSearchQuery('')}
        MenuProps={{
          // Keep focus in the search box instead of the option list.
          autoFocus: false,
          slotProps: { paper: { elevation: 2, sx: { maxHeight: 400 } } }
        }}
      >
        <ListSubheader role="presentation" sx={{ p: 0, lineHeight: 'unset' }}>
          <FacilitySearch
            autoFocus
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (isKeyOwnedBySearchBox(event.key)) {
                event.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {isSelectedOptionHidden && (
          <MenuItem value={value} sx={{ display: 'none' }} />
        )}
        {matchingOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
        {matchingOptions.length === 0 && (
          <MenuItem disabled>No matching views found</MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
